import { useApi } from "@core/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@core/components/ui/button";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import { useTranslation } from "react-i18next";
import { LabeledComboBox } from "@core/components/forms/LabeledComboBox";
import { useAuthConfig } from "@core/hooks/useAuthConfig";
import { minutes } from "@core/helpers";
import { cn } from "@core/lib/utils";
import { clearSiteTopBannerHeight, setSiteTopBannerHeight } from "@site/components/siteTopBanners";

export const EmulationBanner = () => {
    const api = useApi();
    const queryClient = useQueryClient();
    const authSettings = useAuthConfig();
    const ignoreUser = authSettings?.config?.AccountOnlyEmulation ?? false;
    const { user, isLoading, refetch: refetchUser } = useCurrentUser();
    const { t } = useTranslation();
    const { data } = useQuery({
        queryKey: ["roleCheck", "Super Admin"],
        queryFn: () =>
            Promise.all([
                api["/auth/role-check/{role}"]("Super Admin"),
            ]),
    });
    const { data: currentUserCanEmulate } = useQuery({
        queryKey: ["emulation", "currentUserIsAble"],
        queryFn: () => api["/emulation/current-user/is-allowed"](),
        staleTime: minutes(1),
    });
    const [isSuperAdmin] = data ?? [false];
    const bannerRef = useRef<HTMLDivElement | null>(null);
    const [customerSearch, setCustomerSearch] = useState("");
    const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState(customerSearch);
    
    // Super Admin is the only Ordering Portal emulate role (TASK-a81116).
    const dynamicText = useMemo(() => {
        const bannerTitle = t("ui.emulate", "Emulate");
        const startButtonText = t("ui.Emulate", "Emulate");
        const stopButtonText = t("ui.StopEmulation", "Stop Emulation");
        const activeText = t("ui.Emulating", "Emulating");
        return { bannerTitle, startButtonText, stopButtonText, activeText };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ isSuperAdmin ]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedCustomerSearch(customerSearch);
        }, 500);
        return () => clearTimeout(timeout);
    }, [customerSearch]);

    useEffect(() => {
        const updateOffset = () => {
            setSiteTopBannerHeight("emulation", bannerRef.current?.offsetHeight ?? 0);
        };

        updateOffset();
        const observer = new ResizeObserver(() => updateOffset());
        if (bannerRef.current) {
            observer.observe(bannerRef.current);
        }
        window.addEventListener("resize", updateOffset);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateOffset);
            clearSiteTopBannerHeight("emulation");
        };
    }, []);

    const { data: searchedCustomers } = useQuery({
        queryKey: ["emulatableCustomers", debouncedCustomerSearch],
        enabled: currentUserCanEmulate?.IsSuccess,
        queryFn: async () => {
            try {
                const dto = { PageIndex: 0, PageSize: 30, NameContains: debouncedCustomerSearch };
                return await api.GetEmulatableCustomers(dto);
            } catch (error) {
                console.error("_site.route", error);
                return null;
            }
        }
    });

    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

    const { data: emulationState, refetch: refetchEmulationState } = useQuery({
        queryKey: ["emulationState"],
        queryFn: async () => {
            if (!user && !isLoading) {
                stopEmulation.mutate();
            }
            return await api.GetEmulationState();
        },
    });

    useEffect(() => {
        if (isLoading) return;
        if (!user && emulationState?.IsSuccess) {
            stopEmulation.mutate();
            location.reload();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isLoading, emulationState]);

    const { data: emulatableUsers, isLoading: usersLoading } = useQuery({
        queryKey: ["emulatableUsers", selectedCustomerId],
        queryFn: async () => {
            if (!selectedCustomerId) return { Results: [] };
            const dto = { PageIndex: 0, PageSize: 10 };
            return await api.GetEmulatableUsers(dto, selectedCustomerId);
        },
        enabled: !!selectedCustomerId,
    });

    const startEmulation = useMutation({
        mutationFn: async () => {
            if (!selectedCustomerId || (!ignoreUser && !selectedUserId)) return;
            await api.StartEmulation({ CustomerId: selectedCustomerId, UserId: selectedUserId ?? undefined });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["emulationState"] });
            refetchUser();
            location.reload();
        },
    });

    const stopEmulation = useMutation({
        mutationFn: api.StopEmulation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["emulationState"] });
            refetchUser();
            location.reload();
        },
    });
    const selectedCustomer = searchedCustomers?.Results?.find(c => c.Id === selectedCustomerId);

    return (
        <div ref={bannerRef} data-testid="emulation-banner" className="fixed top-0 left-0 right-0 z-[250] flex flex-col sm:flex-row justify-between items-center py-1 px-2 print:hidden" style={{ background: "#0F2942", borderBottom: "1px solid #1a3a52" }}>
            <style>{`
                .emu-customer-popover { background: #ffffff !important; color: #171717 !important; }
                .emu-customer-popover [cmdk-root] { background: #ffffff !important; color: #171717 !important; }
                .emu-customer-popover [cmdk-input] {
                    color: #171717 !important;
                    background: transparent !important;
                    caret-color: #171717 !important;
                    -webkit-text-fill-color: #171717 !important;
                }
                .emu-customer-popover [cmdk-input]::placeholder { color: #6b7280 !important; opacity: 1 !important; }
                .emu-customer-popover [cmdk-input]::-webkit-input-placeholder { color: #6b7280 !important; opacity: 1 !important; }
                .emu-customer-popover [cmdk-input]::-moz-placeholder { color: #6b7280 !important; opacity: 1 !important; }
                .emu-customer-popover [cmdk-item] { color: #171717 !important; }
                .emu-customer-popover [cmdk-empty] { color: #4b5563 !important; }
            `}</style>
            <span className="text-sm font-semibold my-2 mx-2 tracking-widest uppercase" style={{ color: "#D4AF37", fontFamily: "'Outfit', sans-serif", letterSpacing: "0.12em" }}>
                {dynamicText.bannerTitle}
            </span>
            {!emulationState?.IsSuccess ? (
                <div className="flex flex-row flex-wrap items-center gap-2 w-full sm:w-auto">
                    <div className="relative z-60 min-w-0 flex-1 sm:flex-initial text-neutral-900">
                        <LabeledComboBox
                            label=""
                            className={cn(
                                "isolate w-full min-w-0 rounded-md bg-white",
                                "[&_button]:border-neutral-300 [&_button]:!text-neutral-900",
                                "[&_button_span]:text-neutral-900 [&_button_svg]:text-neutral-700"
                            )}
                            popoverClassName={cn(
                                "emu-customer-popover",
                                "z-[260] w-[min(100vw-2rem,28rem)] !border-neutral-200 !p-0 shadow-lg"
                            )}
                            items={searchedCustomers?.Results || []}
                            closeOnSelect
                            isSelected={(item) => item.Id === selectedCustomerId}
                            onItemToggle={(item) => {
                                setSelectedCustomerId(item.Id);
                                setCustomerSearch(item.Name ?? item.Key ?? item.Id.toString());
                                setSelectedUserId(null);
                            }}
                            renderItem={(item) => [item.Key, item.Name].filter(Boolean).join(" | ")}
                            getItemValue={(item) => [item.Key, item.Name].filter(Boolean).join(" | ")}
                            searchPlaceholder={t("ui.searchEllipses", "Search...")}
                            searchInputWrapperClassName="!px-0"
                            placeholder={selectedCustomer ? [selectedCustomer.Key, selectedCustomer.Name].filter(Boolean).join(" | ") : t("ui.SelectACustomer", "Select Customer...")}
                            onSearchChange={setCustomerSearch}
                            itemSize={62}
                        />
                    </div>
                    {!ignoreUser && (<select
                        className="px-3 py-2 border rounded bg-white text-gray-700 shrink-0"
                        disabled={!selectedCustomerId || usersLoading}
                        onChange={(e) => setSelectedUserId(Number(e.target.value))}
                        value={selectedUserId ?? ""}
                    >
                        <option value="">{t("ui.SelectUser", "Select User")}{"..."}</option>
                        {emulatableUsers?.Results?.map((user) => (
                            <option key={user.Id} value={user.Id}>
                                {user.Name || user.Key || `User #${user.Id}`}
                            </option>
                        ))}
                    </select>)}
                    <Button
                        variant="primary"
                        className="rounded disabled:opacity-50 shrink-0 font-semibold text-sm"
                        style={{ background: "#D4AF37", color: "#0F2942", border: "none", fontFamily: "'Outfit', sans-serif" }}
                        disabled={!selectedCustomerId || (!selectedUserId && !ignoreUser) || startEmulation.isPending}
                        onClick={() => startEmulation.mutate(undefined, {
                            onSuccess: () => refetchEmulationState(),
                        })}
                    >
                        {dynamicText.startButtonText}
                    </Button>
                </div>
            ) : (
                <div className="flex items-center">
                    <span className="mx-3 text-sm font-medium" style={{ color: "rgba(255,255,255,0.85)", fontFamily: "'Outfit', sans-serif" }}>
                        {dynamicText.activeText}&nbsp;<span style={{ color: "#D4AF37", fontWeight: 600 }}>{ignoreUser
                          ? (emulationState?.Result?.CustomerName ?? "—")
                          : (emulationState?.Result?.UserEmail || emulationState?.Result?.UserName || "—")}</span>
                    </span>
                    <Button
                        variant="secondary"
                        className="px-4 py-1 rounded disabled:opacity-50 text-sm font-semibold"
                        style={{ background: "transparent", color: "#D4AF37", border: "1px solid #D4AF37", fontFamily: "'Outfit', sans-serif" }}
                        disabled={stopEmulation.isPending}
                        onClick={() => stopEmulation.mutate()}
                    >
                        {dynamicText.stopButtonText}
                    </Button>
                </div>
            )}
        </div>
    );
};